import { openSplitViewState, Text } from '@approvalmax/ui/src/components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'modules/react-redux';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { memo, useCallback, useMemo } from 'react';
import { splitViewGoToBarDefaultSettings, splitViewGoToBarSettingsState } from 'shared/components';
import { useNetSuiteBillPaymentApplyListFieldSettings } from 'shared/hooks';
import {
    activeAttachmentIdState,
    ContentSplitViewMode,
    contentSplitViewState,
    netSuiteBillPaymentApplyListState,
    netSuiteBillPaymentFieldValuesState,
} from 'shared/states';

import { SectionContainer } from '../../../../components/SectionContainer';
import { useChangeNetSuiteRequest, useCurrencyPrecision } from '../../hooks';
import { useNetSuiteBillApplyListChanges } from '../../NetSuiteBillPaymentRequestForm/NetSuiteBillPaymentRequestForm.hooks';
import {
    netSuiteBillPaymentApplyListRequestIdsWithPaymentErrorState,
    netSuiteBillPaymentApplyListRequestIdsWithStatusErrorState,
} from '../../NetSuiteBillPaymentRequestForm/NetSuiteBillPaymentRequestForm.states';
import { StyledBillPaymentLineItemsTable } from '../../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import { netSuiteShowErrorsState } from '../../resources/states';
import { getColumnDefinitions } from './NetSuiteApplyList.config';
import { messages } from './NetSuiteApplyList.messages';

export const NetSuiteApplyList = memo(() => {
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const activeAttachmentId = useAtomValue(activeAttachmentIdState);
    const setOpenSplitView = useSetAtom(openSplitViewState);
    const [applyList, setApplyList] = useAtom(netSuiteBillPaymentApplyListState);
    const [contentSplitView, setContentSplitView] = useAtom(contentSplitViewState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);
    const requestIdsWithStatusError = useAtomValue(netSuiteBillPaymentApplyListRequestIdsWithStatusErrorState);
    const requestIdsWithPaymentError = useAtomValue(netSuiteBillPaymentApplyListRequestIdsWithPaymentErrorState);
    const fieldValues = useAtomValue(netSuiteBillPaymentFieldValuesState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    const { documentFields } = useSelector(getNetSuiteContext);

    const fieldSettings = useNetSuiteBillPaymentApplyListFieldSettings(documentFields);

    const onSelectAttachment = useCallback(
        (attachmentId: string, billRequestId: string, billFriendlyName: string) => {
            setOpenSplitView(true);
            setContentSplitView({
                mode: ContentSplitViewMode.AttachmentsToNetSuiteBillLinkedToBillPayment,
                attachmentId,
                billId: billRequestId,
                billFriendlyName: billFriendlyName,
            });
            setSplitViewGoToBarSettings({
                show: true,
                targetContent: {
                    mode: ContentSplitViewMode.NetSuiteBillLinkedToBillPayment,
                    billId: billRequestId,
                },
                targetContentName: billFriendlyName,
            });
        },
        [setOpenSplitView, setContentSplitView, setSplitViewGoToBarSettings]
    );

    const onSelectBill = useCallback(
        (billRequestId: string) => {
            setContentSplitView({
                mode: ContentSplitViewMode.NetSuiteBillLinkedToBillPayment,
                billId: billRequestId,
            });
            setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
            setOpenSplitView(true);
        },
        [setContentSplitView, setOpenSplitView, setSplitViewGoToBarSettings]
    );

    const onRemoveLine = useCallback(
        (lineId: string) => {
            let requestId: string | null | undefined;

            setApplyList((currentApplyList) =>
                currentApplyList.filter((item) => {
                    if (item.id !== lineId) {
                        return true;
                    }

                    requestId = item.requestId;

                    return false;
                })
            );

            changeNetSuiteRequest();

            const isBillInSplitView =
                contentSplitView.mode === ContentSplitViewMode.NetSuiteBillLinkedToBillPayment &&
                contentSplitView.billId === requestId;
            const isAttachmentOfBillInSplitView =
                contentSplitView.mode === ContentSplitViewMode.AttachmentsToNetSuiteBillLinkedToBillPayment &&
                contentSplitView.billId === requestId;

            if (openSplitViewState && (isBillInSplitView || isAttachmentOfBillInSplitView)) {
                setOpenSplitView(false);
                setContentSplitView({ mode: ContentSplitViewMode.Empty });
            }
        },
        [contentSplitView, changeNetSuiteRequest, setApplyList, setContentSplitView, setOpenSplitView]
    );

    const { onApplyListDiscountAmountChange, onApplyListPaymentAmountChange } = useNetSuiteBillApplyListChanges();

    const columnDefinitions = useMemo(
        () =>
            getColumnDefinitions({
                showErrors,
                fieldSettings,
                currencyPrecision,
                activeAttachmentId,
                requestIdsWithStatusError,
                requestIdsWithPaymentError,
                onSelectBill,
                onSelectAttachment,
                onApplyListDiscountAmountChange,
                onApplyListPaymentAmountChange,
            }),
        [
            showErrors,
            fieldSettings,
            currencyPrecision,
            activeAttachmentId,
            requestIdsWithStatusError,
            requestIdsWithPaymentError,
            onSelectBill,
            onSelectAttachment,
            onApplyListDiscountAmountChange,
            onApplyListPaymentAmountChange,
        ]
    );

    return (
        <SectionContainer title={<Text font='headline'>{messages.sectionName}</Text>} noPadding>
            <StyledBillPaymentLineItemsTable
                columnDefinitions={columnDefinitions}
                lineItems={applyList}
                invalid={false}
                canReorder={false}
                canAddLine={false}
                onRemoveLine={onRemoveLine}
            />
        </SectionContainer>
    );
});

NetSuiteApplyList.displayName = 'NetSuiteApplyList';
